"use client";

import { User, Target, Award, Filter, Info } from "lucide-react";
import "./MobilityUserProfileHeader.css";
import { useState, useEffect, useRef } from "react";
import Modal from "../layout/Modal";
import Pagination from "../shared/paginator/paginator.js";

import {
  getMobilityOpportunitiesApi,
  createMobilityRecommendationApi,
} from "../../services/upskillingService";
import { handleApiError as baseHandleApiError } from "../shared/util";
import SubscriptionExpiredModal from "../dashboard/SubscriptionExpiredModal";
import { useTranslation } from "react-i18next";
import Multiselect from "multiselect-react-dropdown";
import { getPeopleById } from "../../services/peopleService";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getMobilityOpportunitiesByUserldApi } from "../../services/peopleService";
const MobilityUserProfileHeader = ({ userId }) => {
  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showRecommendModal, setShowRecommendModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [recommendationReason, setRecommendationReason] = useState("");
  const [recommendationComment, setRecommendationComment] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [showModal, setShowModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
  });

  const [searchParams] = useSearchParams();
  const readinessFromUrl = searchParams.getAll("readiness");
  const sourceFromUrl = searchParams.get("source");
  const normalizeList = (arr, fallbackAll = ["All"]) => {
    const cleaned = Array.from(
      new Set((arr || []).map((x) => (x || "").trim()).filter(Boolean)),
    );

    if (!cleaned.length) return fallbackAll;

    const hasReal = cleaned.some((x) => x !== "All");
    if (hasReal) return cleaned.filter((x) => x !== "All");

    return fallbackAll;
  };

  const getInitialMobilityStatus = () => {
    // 1) explicit readiness in URL wins
    if (readinessFromUrl.length) {
      return normalizeList(readinessFromUrl, ["All"]);
    }

    // 2) source-based default
    if (sourceFromUrl === "mobility") {
      return ["All"];
    }

    if (sourceFromUrl === "employee") {
      return ["Mobility-ready", "Mobility-opportunities"];
    }

    // 3) fallback default
    return ["All"];
  };
  // Pagination for suggested opportunities
  // const [itemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [filterMobilityStatus, setFilterMobilityStatus] = useState(
    getInitialMobilityStatus,
  );
  useEffect(() => {
    setFilterMobilityStatus(getInitialMobilityStatus());
    setCurrentPage(1);
  }, [searchParams.toString()]);

  // Readiness multiselect helpers
  const READINESS_OPTIONS = ["All", "Mobility-ready", "Mobility-opportunities"];
  const [openReadiness, setOpenReadiness] = useState(false);
  const readinessRef = useRef(null);

  // Readiness options
  const READINESS_LABELS = ["All", "Mobility-ready", "Mobility-opportunities"];
  const READINESS_OPTION_OBJS = READINESS_LABELS.map((key) => ({
    cat: "Readiness",
    key,
  }));

  const toOptionObj = (key) => READINESS_OPTION_OBJS.find((o) => o.key === key);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (readinessRef.current && !readinessRef.current.contains(e.target)) {
        setOpenReadiness(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleSelect = (selectedList, selectedItem) => {
    if (selectedItem.key === "All") {
      // "All" is exclusive
      setFilterMobilityStatus(["All"]);
      setCurrentPage(1);
      return;
    }
    // remove "All" and keep specific selections
    const keys = selectedList.map((i) => i.key).filter((k) => k !== "All");
    setFilterMobilityStatus(keys.length ? keys : ["All"]);
    setCurrentPage(1);
  };

  const handleRemove = (selectedList, removedItem) => {
    const keys = selectedList.map((i) => i.key).filter((k) => k !== "All");
    // if user cleared everything, fall back to "All"
    setFilterMobilityStatus(keys.length ? keys : ["All"]);
    setCurrentPage(1);
  };

  const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");

  const navigate = useNavigate();
  const onNavigate = (page) => {
    console.log("Navigating to:", page);
    navigate(page);
  };

  const handleSubscriptionRedirect = () =>
    new Promise((resolve) => {
      setTimeout(() => {
        navigate("/pricing");
        resolve();
      }, 100);
    });

  const handleApiError = (error, options) => {
    if (error?.response?.status === 403) {
      setSubscriptionMessage(
        error?.response?.data?.message ||
          "Please upgrade your plan to continue using this feature.",
      );
      setShowSubscriptionExpired(true);
      return;
    }
    return baseHandleApiError(error, options);
  };

  useEffect(() => {
    if (userId) {
      fetchUserMobilityData();
    }
  }, [userId, filterMobilityStatus, currentPage, itemsPerPage]);

  const fetchUserMobilityData = async () => {
    try {
      setIsLoading(true);
      const obj = {
        pageNo: currentPage,
        pageSize: itemsPerPage,
        userId: [userId],
        type:
          Array.isArray(filterMobilityStatus) &&
          filterMobilityStatus.length &&
          !filterMobilityStatus.includes("All")
            ? filterMobilityStatus
            : undefined,
      };
      // const response = await getMobilityOpportunitiesApi(obj);
      const response = await getMobilityOpportunitiesByUserldApi(obj);
      const rows = Array.isArray(response?.data) ? response.data : [];
      const count = Number(response?.totalCount ?? rows.length ?? 0);
      setTotalCount(count);

      if (rows.length > 0) {
        const userMobilityData = rows[0];
        setUserData({
          name: userMobilityData.name || "Employee",
          currentPosition: userMobilityData.currentPosition || "—",
          currentDepartment: userMobilityData.currentDepartment || "—",
          mobilityScore: userMobilityData.totalMatchPercentage ?? null,
          suggestedPositions: rows.map((item, index) => ({
            id: (currentPage - 1) * itemsPerPage + index + 1,
            title: item.suggestedPosition,
            department: item.suggestedDepartment,
            matchScore: item.totalMatchPercentage,
            reason:
              item.explainability ||
              "AI-recommended based on skills and experience",
            mobilityOpportunityId: item.mobilityOpportunityId,
            userId: item.userId,
            fullData: item,
          })),
        });
      } else {
        // No opportunities -> fetch identity so we can still show header info
        try {
          const profile = await getPeopleById(userId);
          const p = profile?.data ?? profile ?? {};
          setUserData({
            name: p.name || "Employee",
            currentPosition: p.designation || "—",
            currentDepartment: p.department || "—",
            mobilityScore: null,
            suggestedPositions: [],
          });
        } catch {
          setUserData({
            name: "Employee",
            currentPosition: "—",
            currentDepartment: "—",
            mobilityScore: null,
            suggestedPositions: [],
          });
        }
      }
    } catch (error) {
      console.error("Error fetching user mobility data:", error);
      handleApiError(error, { showModal: false });
      // Ensure UI can still render an error state safely
      setUserData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };
  const handlePrevPage = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNextPage = () =>
    setCurrentPage((p) => Math.min(totalPages, p + 1));

  const handleRecommendMove = (position) => {
    setSelectedOpportunity(position);
    setRecommendationReason("");
    setRecommendationComment("");
    setShowRecommendModal(true);
  };

  const submitRecommendation = async () => {
    try {
      console.log("Submitting recommendation for:", selectedOpportunity);
      const obj = {
        mobilityOpportunityId: selectedOpportunity?.mobilityOpportunityId,

        userId: selectedOpportunity.fullData._id,
        reason: recommendationReason,
        comments: recommendationComment,
      };
      const response = await createMobilityRecommendationApi(obj);
      setShowRecommendModal(false);
      if (response.code == 201) {
        setShowModal({
          show: true,
          showButton: false,
          type: "success",
          title: t("mobilityPage.recommendationModal.successTitle"),
          message: t("mobilityPage.recommendationModal.successMessage", {
            name: userData.name,
          }),
        });
        setSelectedOpportunity(null);
        setRecommendationReason("");
        setRecommendationComment("");
      }
    } catch (error) {
      console.error("Error submitting recommendation:", error);
      handleApiError(error, { showModal: true });
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return "mobility-profile-score-excellent";
    if (score >= 80) return "mobility-profile-score-good";
    return "mobility-profile-score-average";
  };

  const getMatchColor = (percentage) => {
    if (percentage >= 90) return "mobility-profile-match-excellent";
    if (percentage >= 80) return "mobility-profile-match-good";
    return "mobility-profile-match-average";
  };

  const generateExplainabilityText = (explainability) => {
    if (!explainability || typeof explainability !== "string") return "";

    // normalize CRLF -> LF
    const str = explainability.trim().replace(/\r\n/g, "\n");

    // insert a newline before any "•" that's not already at the start of a line
    const withBulletBreaks = str.replace(/([^\n])\s*•/g, "$1\n•");

    // avoid accidental double newlines before bullets
    return withBulletBreaks.replace(/\n{2,}•/g, "\n•");
  };

  if (isLoading) {
    return (
      <div className="mobility-profile-container">
        <div className="container">
          {/* Header skeleton */}
          <div className="mobility-profile-header">
            <div className="mobility-profile-avatar skeleton skeleton-avatar" />
            <div className="mobility-profile-info" style={{ width: "100%" }}>
              <div
                className="skeleton skeleton-text"
                style={{ width: 280, height: 28, marginBottom: 8 }}
              />
              <div
                className="skeleton skeleton-text"
                style={{ width: 220, height: 18, marginBottom: 6 }}
              />
              <div
                className="skeleton skeleton-text"
                style={{ width: 260, height: 16 }}
              />
            </div>
            <div
              className="mobility-profile-score-section"
              style={{ minWidth: 120 }}
            >
              <div
                className="skeleton skeleton-chip"
                style={{ width: 100, height: 32 }}
              />
            </div>
          </div>

          {/* Section header skeleton */}
          <div className="mobility-profile-suggestions">
            <div className="mobility-profile-suggestions-header">
              <div
                className="skeleton skeleton-text"
                style={{ width: 28, height: 24 }}
              />
              <div
                className="skeleton skeleton-text"
                style={{ width: 260, height: 24 }}
              />
            </div>

            {/* Cards grid skeleton */}
            <div className="mobility-profile-cards-grid">
              {[1, 2, 3].map((i) => (
                <div key={i} className="mobility-profile-suggestion-card">
                  <div className="mobility-profile-card-header">
                    <div
                      className="mobility-profile-card-icon skeleton"
                      style={{ width: 48, height: 48 }}
                    />
                    <div
                      className="mobility-profile-card-content"
                      style={{ width: "100%" }}
                    >
                      <div
                        className="skeleton skeleton-text"
                        style={{ width: "60%", height: 18, marginBottom: 8 }}
                      />
                      <div
                        className="skeleton skeleton-text"
                        style={{ width: "40%", height: 14 }}
                      />
                    </div>
                  </div>
                  <div className="mobility-profile-card-actions">
                    <div
                      className="mobility-profile-btn skeleton"
                      style={{ height: 38 }}
                    />
                    <div
                      className="mobility-profile-btn skeleton"
                      style={{ height: 38 }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mobility-profile-container">
      <div className="container">
        {/* User Profile Header */}
        <div className="mobility-profile-header">
          <div className="mobility-profile-avatar">
            <User size={48} />
          </div>
          <div className="mobility-profile-info">
            <h1 className="mobility-profile-name">{userData.name}</h1>
            <p className="mobility-profile-position">
              {userData.currentPosition}
            </p>
            <p className="mobility-profile-department">
              {userData.currentDepartment}
            </p>
          </div>
        </div>

        <div
          className="mobility-profile-filters"
          style={{ marginBottom: "20px" }}
        >
          <div className="ufilter-group mobility-readiness-filter-group">
            <Filter size={16} />

            <label>{t("mobilityPage.filters.readiness")}</label>

            <div className="mobility-readiness-select-wrap">
              <Multiselect
                displayValue="key"
                options={READINESS_OPTION_OBJS}
                onSelect={handleSelect}
                onRemove={handleRemove}
                onKeyPressFn={() => {}}
                onSearch={() => {}}
                placeholder=""
                selectedValues={(filterMobilityStatus.includes("All")
                  ? ["All"]
                  : filterMobilityStatus
                ).map(toOptionObj)}
                showCheckbox
                closeOnSelect={false}
                style={{
                  multiselectContainer: {
                    width: "100%",
                    minWidth: 0,
                  },
                  searchBox: {
                    width: "100%",
                    minWidth: 0,
                    minHeight: "44px",
                    boxSizing: "border-box",
                  },
                  chips: {
                    width: "auto",
                    maxWidth: "100%",
                    whiteSpace: "nowrap",
                  },
                  optionContainer: {
                    width: "100%",
                    boxSizing: "border-box",
                  },
                }}
              />
            </div>
          </div>
        </div>

        {/* Suggested Positions Section */}
        <div className="mobility-profile-suggestions">
          <div className="mobility-profile-suggestions-header">
            <Target size={24} />
            <h2>Suggested Career Opportunities</h2>
          </div>

          <div className="mobility-profile-cards-grid">
            {userData.suggestedPositions.length === 0 ? (
              <div className="mobility-empty-state">
                No opportunities found for the selected readiness filter.
              </div>
            ) : (
              userData.suggestedPositions.map((position) => (
                <div
                  key={position.id}
                  className="mobility-profile-suggestion-card"
                >
                  <div className="mobility-profile-card-header">
                    <div className="mobility-profile-card-main-row">
                      <div
                        className="mobility-profile-card-icon"
                        style={{ marginRight: "20px" }}
                      >
                        <Award size={24} />
                      </div>
                      <div className="mobility-profile-card-content">
                        <h3 className="mobility-profile-card-title">
                          {position.title}
                        </h3>
                        <p className="mobility-profile-card-department">
                          {position.department}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div
                    className="mobility-profile-match-score-container"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      className={`mobility-profile-card-score ${getMatchColor(
                        position.matchScore,
                      )}`}
                      aria-label={`View match explanation for ${position.title}`}
                    >
                      {position.matchScore}% Match{" "}
                      <Info size={12} className="mobility-score-info-icon" />
                    </button>

                    <div
                      className="mobility-profile-score-hover-box"
                      role="tooltip"
                    >
                      <p
                        dangerouslySetInnerHTML={{
                          __html: generateExplainabilityText(
                            position.reason,
                          ).replace(/\n/g, "<br/>"),
                        }}
                      />
                    </div>
                  </div>

                  <div className="mobility-profile-card-actions">
                    <button
                      className="mobility-profile-btn mobility-profile-btn-primary"
                      onClick={() => handleRecommendMove(position)}
                    >
                      Recommend
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {totalCount > itemsPerPage && (
            <Pagination
              loading={isLoading} // ← NEW
              total={totalCount}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              currentPageCount={userData.suggestedPositions.length}
              onPageChange={(page) => setCurrentPage(page)}
              onPageSizeChange={(size) => {
                setItemsPerPage(size);
                const newTotalPages = Math.max(
                  1,
                  Math.ceil((totalCount || 0) / size),
                );
                if (currentPage > newTotalPages) setCurrentPage(newTotalPages);
              }}
              infoRenderer={({ start, end, total }) =>
                t("mobilityPage.paginationInfo", { start, end, total })
              }
            />
          )}
        </div>
      </div>

      {/* Recommendation Modal */}
      {showRecommendModal && selectedOpportunity && (
        <Modal
          show={showRecommendModal}
          onClose={() => setShowRecommendModal(false)}
          type="info"
          title={t("mobilityPage.recommendationModal.title")}
          showButton={false}
        >
          <div className="mobility-recommendation-modal-content">
            <div className="mobility-recommendation-info">
              <div className="mobility-recommendation-employee">
                <div className="mobility-recommendation-avatar">
                  <User size={32} />
                </div>
                <div className="mobility-recommendation-details">
                  <h4>{userData.name}</h4>
                  <p>
                    {userData.currentPosition} → {selectedOpportunity.title}
                  </p>
                </div>
              </div>
            </div>

            <div className="mobility-reason-section">
              <label className="mobility-reason-label">
                {t("mobilityPage.recommendationModal.primaryReason")}
              </label>
              <div className="mobility-reason-options">
                <label className="mobility-reason-option">
                  <input
                    type="radio"
                    name="recommendationReason"
                    value="Soft Skills Development"
                    checked={recommendationReason === "Soft Skills Development"}
                    onChange={(e) => setRecommendationReason(e.target.value)}
                  />
                  <div className="mobility-reason-content">
                    <strong>
                      {t(
                        "mobilityPage.recommendationModal.softSkillsDevelopment",
                      )}
                    </strong>
                    <p>
                      {t(
                        "mobilityPage.recommendationModal.softSkillsDescription",
                      )}
                    </p>
                  </div>
                </label>
                <label className="mobility-reason-option">
                  <input
                    type="radio"
                    name="recommendationReason"
                    value="Technical/Job Skills"
                    checked={recommendationReason === "Technical/Job Skills"}
                    onChange={(e) => setRecommendationReason(e.target.value)}
                  />
                  <div className="mobility-reason-content">
                    <strong>
                      {t("mobilityPage.recommendationModal.technicalJobSkills")}
                    </strong>
                    <p>
                      {t(
                        "mobilityPage.recommendationModal.technicalJobSkillsDescription",
                      )}
                    </p>
                  </div>
                </label>
              </div>
            </div>

            <div className="mobility-comment-section">
              <label
                htmlFor="recommendation-comment"
                className="mobility-comment-label"
              >
                {t("mobilityPage.recommendationModal.additionalComments")}
              </label>
              <textarea
                id="recommendation-comment"
                value={recommendationComment}
                onChange={(e) => setRecommendationComment(e.target.value)}
                placeholder={t(
                  "mobilityPage.recommendationModal.commentsPlaceholder",
                )}
                className="mobility-comment-textarea"
                rows={4}
              />
            </div>

            <div className="mobility-modal-actions">
              <button
                className="mobility-modal-btn mobility-secondary"
                onClick={() => setShowRecommendModal(false)}
              >
                {t("mobilityPage.recommendationModal.cancel")}
              </button>
              <button
                className="mobility-modal-btn mobility-primary"
                onClick={submitRecommendation}
                disabled={!recommendationReason}
              >
                {t("mobilityPage.recommendationModal.submitRecommendation")}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Success/Error Modal */}
      <Modal
        showButton={showModal.showButton}
        show={showModal.show}
        onClose={() =>
          setShowModal({
            show: false,
            type: "",
            title: "",
            message: "",
          })
        }
        type={showModal.type}
        title={showModal.title}
        message={showModal.message}
      />

      <SubscriptionExpiredModal
        show={showSubscriptionExpired}
        message={subscriptionMessage}
        onRedirect={handleSubscriptionRedirect}
      />
    </div>
  );
};

export default MobilityUserProfileHeader;
